import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Search, Pin, Info, X, Maximize2, MessageCircle, FolderOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import { WhatsAppButton } from "../WhatsApp";
import { useChat, useConversations, isOnline, dayLabel } from "./useChat";
import { ChatList, Avatar } from "./ChatList";
import { Bubble } from "./Bubble";
import { Composer } from "./Composer";
import { GroupDialog, ForwardDialog, StarredDialog } from "./ChatDialogs";
import { MediaDialog } from "./MediaDialog";
import { SaveToProjectDialog } from "./SaveToProject";
import { ReadByDialog } from "./ReadBy";

function subtitle(conv, chat, typing) {
  if (typing?.length) return <span className="text-emerald-600">{typing.join(", ")} typing…</span>;
  if (conv.type === "dm") return isOnline(conv.user) ? "online" : conv.user?.last_seen ? `last seen ${new Date(conv.user.last_seen).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}` : "Direct message";
  if (conv.type === "group") return conv.group.members.map((id) => chat.state.users.find((u) => u.id === id)?.name).filter(Boolean).join(", ");
  return conv.type === "project" ? `Project channel · ${conv.sub}` : "Studio-wide channel";
}

function tickFor(m, chat, me) {
  const ch = m.channel;
  const others = (chat.state.members[ch] || []).filter((id) => id !== me);
  if (!others.length) return "sent";
  const reads = chat.state.reads[ch] || {};
  if (others.every((id) => (reads[id] || "") >= m.created_at)) return "read";
  const seen = (id) => chat.state.users.find((u) => u.id === id)?.last_seen || "";
  return others.some((id) => seen(id) >= m.created_at) ? "delivered" : "sent";
}

function Conversation({ conv, chat, me, isAdmin, onBack, onInfo }) {
  const [reply, setReply] = useState(null);
  const [editing, setEditing] = useState(null);
  const [fwd, setFwd] = useState(null);
  const [q, setQ] = useState(null);
  const [media, setMedia] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [infoMsg, setInfoMsg] = useState(null);
  const feed = useRef(null);
  const count = useRef(0);
  const msgs = q ? chat.msgs.filter((m) => !m.deleted && (m.text || "").toLowerCase().includes(q.toLowerCase())) : chat.msgs;
  useEffect(() => { setReply(null); setEditing(null); setQ(null); count.current = 0; }, [conv.key]);
  useEffect(() => {
    const el = feed.current;
    if (!el || q) return;
    const near = el.scrollHeight - el.scrollTop - el.clientHeight < 160;
    if (chat.msgs.length !== count.current && (near || count.current === 0 || chat.msgs[chat.msgs.length - 1]?.author_id === me)) el.scrollTop = el.scrollHeight;
    count.current = chat.msgs.length;
  }, [chat.msgs, q, me]);
  const act = async (key, m, emoji) => {
    if (key === "react") return chat.react(m.id, emoji);
    if (key === "reply") return setReply(m);
    if (key === "forward") return setFwd(m);
    if (key === "star") return chat.star(m.id);
    if (key === "saveproj") return setSaveMsg(m);
    if (key === "info") return setInfoMsg(m);
    if (key === "track") return chat.track(m.id, m.track_reads);
    if (key === "pintop") return chat.pinTop(m.id, m.pinned_top);
    if (key === "edit") return setEditing(m);
    if (key === "delete" && window.confirm("Delete this message for everyone?")) return chat.remove(m.id);
  };
  act.canDeleteAny = isAdmin;
  act.canInfo = conv.type !== "dm";
  act.canPinTop = conv.type !== "dm" && (isAdmin || (conv.type === "group" && conv.group.created_by === me));
  const topPin = chat.msgs.find((m) => m.pinned_top && !m.deleted);
  const pinned = chat.state.pins.includes(conv.key);
  return (
    <section data-testid="chat-conversation" className="flex h-full min-w-0 flex-1 flex-col">
      <header className="flex items-center gap-3 border-b border-zinc-200 bg-[#F6F1E6] px-3 py-2.5">
        <button data-testid="chat-back-button" onClick={onBack} className="rounded-full p-1.5 text-zinc-600 hover:bg-black/5 md:hidden"><ArrowLeft className="h-5 w-5" /></button>
        <button onClick={conv.type === "group" ? onInfo : undefined} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <Avatar name={conv.name} type={conv.type} size="h-10 w-10" online={conv.type === "dm" && isOnline(conv.user)} />
          <div className="min-w-0"><div data-testid="chat-channel-title" className="truncate text-[15px] font-semibold text-zinc-900">{conv.name}</div><div data-testid="chat-channel-subtitle" className="truncate text-xs text-zinc-500">{subtitle(conv, chat, chat.state.typing[conv.key])}</div></div>
        </button>
        {conv.type === "project" && <WhatsAppButton testid="chat-client-whatsapp" iconOnly variant="ghost" phone={conv.project.client_whatsapp || ""} projectId={conv.project.id} text={`Hello ${conv.project.client || ""},\n\nUpdate on ${conv.project.name}: `} context="Chat update" />}
        <button data-testid="chat-media-button" title="Media, docs & links" onClick={() => setMedia(true)} className="rounded-full p-2 text-zinc-600 hover:bg-black/5"><FolderOpen className="h-5 w-5" /></button>
        <button data-testid="chat-search-toggle" title="Search messages" onClick={() => setQ(q === null ? "" : null)} className="rounded-full p-2 text-zinc-600 hover:bg-black/5"><Search className="h-5 w-5" /></button>
        <button data-testid="chat-pin-toggle" title={pinned ? "Unpin chat" : "Pin chat"} onClick={() => chat.pin(conv.key)} className={`rounded-full p-2 hover:bg-black/5 ${pinned ? "text-[#A9792C]" : "text-zinc-600"}`}><Pin className={`h-5 w-5 ${pinned ? "fill-current" : ""}`} /></button>
        {conv.type === "group" && <button data-testid="chat-group-info" title="Group info" onClick={onInfo} className="rounded-full p-2 text-zinc-600 hover:bg-black/5"><Info className="h-5 w-5" /></button>}
      </header>
      {q !== null && (
        <div className="flex items-center gap-2 border-b border-zinc-200 bg-white px-4 py-2">
          <Search className="h-4 w-4 text-zinc-400" /><input autoFocus data-testid="chat-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search in this chat" className="h-8 flex-1 text-sm outline-none" />
          <span data-testid="chat-search-count" className="text-xs text-zinc-400">{q ? `${msgs.length} found` : ""}</span>
          <button data-testid="chat-search-close" onClick={() => setQ(null)}><X className="h-4 w-4 text-zinc-500" /></button>
        </div>
      )}
      {topPin && (
        <div data-testid="chat-pinned-banner" className="flex items-center gap-2 border-b border-zinc-200 bg-white/95 px-4 py-2">
          <Pin className="h-4 w-4 shrink-0 rotate-45 fill-[#15966B] text-[#15966B]" />
          <button data-testid="chat-pinned-open" onClick={() => document.getElementById(`msg-${topPin.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" })} className="min-w-0 flex-1 text-left">
            <div className="text-[11px] font-bold text-[#15966B]">Pinned by {topPin.pinned_by}</div>
            <div className="truncate text-sm text-zinc-700">{topPin.author}: {topPin.text || topPin.attachment?.name}</div>
          </button>
          {act.canPinTop && <button data-testid="chat-pinned-unpin" title="Unpin" onClick={() => chat.pinTop(topPin.id, true)} className="rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><X className="h-4 w-4" /></button>}
        </div>
      )}
      <div ref={feed} data-testid="channel-feed" className="thin-scroll flex-1 space-y-1 overflow-y-auto bg-[#EFE8DA] px-3 py-4 sm:px-[6%]"
        style={{ backgroundImage: "radial-gradient(rgba(169,121,44,0.09) 1px, transparent 1px)", backgroundSize: "18px 18px" }}>
        {msgs.map((m, i) => {
          const prev = msgs[i - 1];
          const newDay = !prev || new Date(prev.created_at).toDateString() !== new Date(m.created_at).toDateString();
          const mine = m.author_id === me || (!m.author_id && m.author === chat.state.users.find((u) => u.id === me)?.name);
          return (
            <div key={m.id} className={!prev || prev.author !== m.author || newDay ? "pt-2" : ""}>
              {newDay && <div className="my-3 flex justify-center"><span className="rounded-lg bg-white/90 px-3 py-1 text-[11.5px] font-medium uppercase tracking-wide text-zinc-500 shadow-sm">{dayLabel(m.created_at)}</span></div>}
              <Bubble testid={`chat-msg-${i}`} m={m} mine={mine} highlight={q} starred={(m.starred_by || []).includes(me)} onAction={act}
                showAuthor={conv.type !== "dm" && (newDay || !prev || prev.author !== m.author)} tick={mine ? tickFor(m, chat, me) : null} />
            </div>
          );
        })}
        {!msgs.length && <div className="mt-16 flex justify-center"><span className="rounded-lg bg-white/90 px-4 py-2 text-sm text-zinc-500 shadow-sm">{q ? "No messages match your search." : "No messages yet. Say hello!"}</span></div>}
      </div>
      <Composer channel={conv.key} label={conv.name} members={(conv.type === "group" ? conv.group.members.map((id) => chat.state.users.find((u) => u.id === id)) : conv.type === "dm" ? [conv.user] : chat.state.users).filter((u) => u && u.id !== me)}
        isChannel={conv.type === "channel" || conv.type === "project"} replyTo={reply} editing={editing} onTyping={chat.typing}
        onCancel={() => { setReply(null); setEditing(null); }}
        onSend={async (body) => { const r = await chat.send({ ...body, reply_to: reply?.id }); if (r) setReply(null); return r; }}
        onSaveEdit={async (t) => { await chat.edit(editing.id, t); setEditing(null); }} />
      <MediaDialog open={media} onOpenChange={setMedia} msgs={chat.msgs} title={conv.name} onSave={(m) => setSaveMsg(m)} />
      <ReadByDialog msg={infoMsg} chat={chat} me={me} onClose={() => setInfoMsg(null)} />
      <SaveToProjectDialog msg={saveMsg} onClose={() => setSaveMsg(null)} defaultProject={conv.project?.id}
        projects={chat.convs.filter((c) => c.type === "project").map((c) => c.project)} />
      <ForwardDialog msg={fwd} onClose={() => setFwd(null)} convs={chat.convs}
        onForward={async (keys) => { for (const k of keys) await chat.send({ text: fwd.text || "", attachment: fwd.attachment, link: fwd.link, forwarded: true }, k); setFwd(null); }} />
    </section>
  );
}

export function WhatsAppChat({ fullScreen = false, onClose }) {
  const { user, isAdmin } = useAuth();
  const { items: projects } = useCollection("projects");
  const chat = useChat();
  const convs = useConversations(chat.state, projects, user.id);
  const [groupDlg, setGroupDlg] = useState(null);
  const [starred, setStarred] = useState(false);
  const conv = convs.find((c) => c.key === chat.channel);
  const others = useMemo(() => (chat.state?.users || []).filter((u) => u.id !== user.id), [chat.state, user.id]);
  chat.convs = convs;
  const nameOf = (k) => convs.find((c) => c.key === k)?.name || k;
  const saveGroup = async (name, members) => {
    const g = groupDlg?.id ? await chat.updateGroup(groupDlg.id, name, members) : await chat.createGroup(name, members);
    if (g) { setGroupDlg(null); if (!groupDlg?.id && g.id) chat.setChannel(`group:${g.id}`); }
  };
  const jump = (m) => { chat.setChannel(m.channel); setTimeout(() => document.getElementById(`msg-${m.id}`)?.scrollIntoView({ block: "center" }), 900); };
  return (
    <div data-testid="chat-panel" className={`flex overflow-hidden bg-white ${fullScreen ? "fixed inset-0 z-50" : "h-[640px] rounded-2xl border border-zinc-200 shadow-sm"}`}>
      <div className={`${conv ? "hidden md:flex" : "flex"} h-full w-full md:w-auto`}>
        <ChatList convs={convs} active={chat.channel} onOpen={chat.setChannel} typing={chat.state?.typing || {}} onClose={onClose}
          onNewGroup={() => setGroupDlg({})} onStarred={() => setStarred(true)} nameOf={nameOf} onOpenMsg={jump} />
      </div>
      {conv && chat.state ? (
        <Conversation conv={conv} chat={chat} me={user.id} isAdmin={isAdmin} onBack={() => chat.setChannel(null)} onInfo={() => setGroupDlg(conv.group)} />
      ) : (
        <div className="hidden flex-1 flex-col items-center justify-center gap-3 border-b-4 border-[#C9A24A] bg-[#FBF7EC] text-center md:flex" data-testid="chat-empty-state">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white shadow-sm"><MessageCircle className="h-9 w-9 text-[#A9792C]" /></span>
          <div className="font-display text-2xl font-bold text-zinc-900">JOV Event Chat</div>
          <p className="max-w-sm text-sm text-zinc-500">Message teammates directly, create groups, and talk in project channels. Send photos, videos, documents, links and voice notes.</p>
          {!fullScreen && <Link data-testid="chat-open-fullscreen" to="/chat" className="mt-2 inline-flex items-center gap-2 rounded-full bg-zinc-900 px-4 py-2 text-sm font-semibold text-[#E8C877]"><Maximize2 className="h-4 w-4" />Open full screen</Link>}
        </div>
      )}
      <GroupDialog open={!!groupDlg} onOpenChange={(o) => !o && setGroupDlg(null)} users={others} group={groupDlg?.id ? groupDlg : null} onSave={saveGroup}
        onLeave={async () => { await chat.leaveGroup(groupDlg.id); setGroupDlg(null); chat.setChannel(null); }} />
      <StarredDialog open={starred} onOpenChange={setStarred} nameOf={nameOf} onOpen={(m) => { setStarred(false); jump(m); }} />
    </div>
  );
}
